import { useCallback, useMemo } from "react";

import { flow } from "fp-ts/lib/function";

import { useBlizzardEvents } from "../../analytics/hooks";
import { ExclusiveLensStatus, MyLensesLensStatus, Visibility } from "../../gql-generated/types";
import { useLensChangeTracker } from "../../lens/hooks/useLensChangeTracker";
import { useSetVisibility } from "../../lens/hooks/useSetVisibility";
import { ROLE_ACTIONS } from "../../shared/constants";
import { useSelectedLensFolderPermissions } from "../../shared/hooks/roles";
import { filterVisibilityOptionsByPermissions, sortVisibilityOptions } from "../models";

import { makeExclusiveLensVisibilityConfig } from "./utils";
import { type LensVisibilityConfig } from "./visibilityConfigs";

const makeDefaultVisibilityOption = (permittedVisibilityOptions: Visibility[]): Visibility | undefined => {
    if (permittedVisibilityOptions.includes(Visibility.VisibilityPublic)) {
        return Visibility.VisibilityPublic;
    }

    if (permittedVisibilityOptions.includes(Visibility.VisibilityDontPublishToSnapchat)) {
        return Visibility.VisibilityDontPublishToSnapchat;
    }

    const fallbackVisibility = permittedVisibilityOptions[0];

    return fallbackVisibility;
};

export interface LensDetailsVisibilityResult {
    lensVisibilityConfig: LensVisibilityConfig;
    changeVisibility: (value: Visibility, onSuccess?: () => void) => Promise<void>;
    changeVisibilityLoading: boolean;
}

const defaultVisibilityOptions = [
    Visibility.VisibilityHidden,
    Visibility.VisibilityOffline,
    Visibility.VisibilityPublic,
];

const DRAFT_STATUSES = new Set([
    MyLensesLensStatus.LensStatusNotPublishedToSnapchat,
    MyLensesLensStatus.LensStatusAvailable,
]);

export const useLensDetailsVisibility = (
    lensId: string,
    exclusiveLensStatus: ExclusiveLensStatus,
    lensStatus?: MyLensesLensStatus,
    lensFolderId?: string,
): LensDetailsVisibilityResult => {
    const {
        selectedLensFolderPermissions: { ACTIONS, VISIBILITY_OPTIONS },
    } = useSelectedLensFolderPermissions();

    const { updateVisibility, updateLensLoading } = useLensChangeTracker(lensId);
    const { setVisibility: setVisibilityMutation, loading: setVisibilityLoading } = useSetVisibility(
        lensId,
        lensFolderId ?? "",
    );

    const { sendBlizzardEvent: sendBlizzardEventHandler } = useBlizzardEvents();

    const isDraftLens = lensStatus ? DRAFT_STATUSES.has(lensStatus) : false;

    const changeVisibility = useCallback(
        async (value: Visibility, onSuccess?: () => void) => {
            sendBlizzardEventHandler({
                event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                pageType: "LENS_DETAILS",
                action: "CHANGE_VISIBILITY",
            });

            if (isDraftLens) {
                await setVisibilityMutation(value);
            } else {
                updateVisibility(value);
            }

            if (typeof onSuccess === "function") {
                onSuccess();
            }
        },
        [updateVisibility, sendBlizzardEventHandler, isDraftLens, setVisibilityMutation],
    );

    const isPermittedToUpdateLensVisibility = ACTIONS.includes(ROLE_ACTIONS.UPDATE_LENS_VISIBILITY);
    const isExclusiveLens =
        exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive ||
        exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusEligible;

    return useMemo(() => {
        const options = flow(
            filterVisibilityOptionsByPermissions(VISIBILITY_OPTIONS),
            sortVisibilityOptions,
        )(defaultVisibilityOptions);

        const visibilityConfig: LensVisibilityConfig = {
            disabled: !isPermittedToUpdateLensVisibility || options.length === 1,
            defaultVisibility: makeDefaultVisibilityOption(options),
            options,
            disabledOptions: {},
            communityDistributionOptions: options.filter(
                (option) => option !== Visibility.VisibilityDontPublishToSnapchat,
            ),
        };

        return {
            lensVisibilityConfig: isExclusiveLens
                ? makeExclusiveLensVisibilityConfig(visibilityConfig)
                : visibilityConfig,
            changeVisibilityLoading: isDraftLens ? setVisibilityLoading : updateLensLoading,
            changeVisibility,
        };
    }, [
        isPermittedToUpdateLensVisibility,
        VISIBILITY_OPTIONS,
        changeVisibility,
        updateLensLoading,
        setVisibilityLoading,
        isExclusiveLens,
        isDraftLens,
    ]);
};
